import styled from '@emotion/styled';
import {
  isClassFeed,
  isEducatorFeed,
  isLessonFeed,
  isTestSeriesFeed,
  isTopicFeed
} from '@utils/feedTypeHelper';
import textHelper from '@utils/textHelper';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import {
  freeReferralbannerStatus,
  updateFreeReferralCookies
} from '@utils/referralHelper';
import GTPEducatorBlock from '@cont/FeedTypeBlock/GTPEducatorBlock';
import FeedTypeBlock from '@cont/FeedTypeBlock/FeedTypeBlock';
import ReferralBannerComponent from '@cont/Settings/Referral/ReferralBanner';
import { segment } from '@actions/analytics';
import {
  REFERRAL_NUDGE_BANNER,
  REFERRAL_TOUCHPOINT_CLICKED
} from '@constants/segment';
import PurchasedBlock from '@cont/StoreListing/PurchasedBlock';
import BrowseNotesTrackCardWrapper from '@cont/Store/TrackingFlow/browsePage/BrowseNotesTrackCardWrapper';

const EnrolledCourses = styled.span`
  color: var(--color-i-green);
  font-weight: ${Fonts.SEMIBOLD};
`;

const CoursesInfo = styled.div`
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
`;

const ReferralWrapper = styled.div`
  margin-top: ${Spacings.SPACING_0B};
  grid-column: span 18;
  h3 {
    font-weight: ${Fonts.HEAVY};
    font-size: ${Fonts.H4};
  }
  h5 {
    font-weight: ${Fonts.LIGHT};
    font-size: ${Fonts.H6};
  }
  button {
    min-height: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
    width: auto;
    white-space: nowrap;
    font-size: ${Fonts.H7};
    margin-top: ${Spacings.SPACING_2B};
  }
  .banner-image-icon {
    max-width: 284px;
    height: 158px;
  }
  .referral-banner-content {
    padding: 18px ${Spacings.SPACING_6B};
  }
  ${mediaQuery.uptoMobile} {
    height: auto;
    grid-column: span 10;
    margin: -60px ${Spacings.SPACING_0B} ${Spacings.SPACING_10B};
    & > .browse-feed-css {
      width: 100%;
      flex-direction: row;
    }
    .banner-image-icon {
      min-width: ${Spacings.SPACING_24B};
      height: 132px;
      margin-top: ${Spacings.SPACING_6B};
      margin-right: ${Spacings.SPACING_4B};
      margin-left: ${Spacings.SPACING_0B};
    }
    h3 {
      font-weight: ${Fonts.SEMIBOLD};
      font-size: ${Fonts.H5};
      line-height: ${Spacings.SPACING_6B};
    }
    h5 {
      font-weight: ${Fonts.LIGHT};
      font-size: ${Fonts.H7};
      line-height: ${Spacings.SPACING_4B};
    }
    button {
      margin-top: ${Spacings.SPACING_0B};
    }
    .referral-banner-content {
      padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
    }
  }
`;

const ReferralBanner = ({ goalInfo }) => {
  const goalUID = goalInfo.uid;
  const referralButtonSelector = document.querySelector(
    '.referral-banner-button'
  );
  if (referralButtonSelector) {
    referralButtonSelector.addEventListener('click', () => {
      segment.track(REFERRAL_TOUCHPOINT_CLICKED, {
        last_primary_source_section: REFERRAL_NUDGE_BANNER
      });
      updateFreeReferralCookies({ showBanner: false });
    });
  }
  return (
    <ReferralWrapper>
      <ReferralBannerComponent
        propGoalUID={goalUID}
        className="browse-feed-css"
        iconUrl="browseIcon.svg"
      />
    </ReferralWrapper>
  );
};

const RenderBrowseFeedBlock = (props) => {
  const {
    item: feed = {},
    index,
    goalInfo = {},
    lastPrimarySource,
    type,
    dataAnalytics,
    hasActiveSubscription,
    isGTP,
    educatorPanelSection,
    openExternalAppActivation,
    enroll
  } = props || {};
  const { goalSlug, uid: goalUID } = goalInfo;
  const feedType = feed.extraBlockInfo?.entityType;
  let feedCount = 0;
  let minFeedCount = 3;
  let seeAllLink = `/goal/${goalSlug}/${goalUID}/${feed.type}`;
  let viewAllHref = '/goal/[goalSlug]/[goalUID]/[type]';
  const feedBlock = { ...feed };
  const feedProps = {};
  const isEducatorFeedBlock = isEducatorFeed(feedType);
  if (isClassFeed(feedType) || isEducatorFeedBlock) {
    feedCount = feed?.extraBlockInfo?.count;
    if (isGTP) {
      minFeedCount = 0;
      seeAllLink = `/goal/${goalSlug}/${goalUID}/classes/all_classes`;
      viewAllHref = '/goal/[goalSlug]/[goalUID]/classes/[type]';
    }
  } else if (feed.type === 'store_purchased_items') {
    return <PurchasedBlock data={feed.data} />;
  } else if (feed.type === 'store_physical_notes_items') {
    return <BrowseNotesTrackCardWrapper data={feed.data} />;
  } else if (feed.type === 'referral' && !hasActiveSubscription) {
    const showFreeReferralBanner = freeReferralbannerStatus();
    if (showFreeReferralBanner?.updateCookies) {
      updateFreeReferralCookies({ showBanner: true });
    } else if (
      !showFreeReferralBanner?.updateCookies &&
      showFreeReferralBanner?.showBanner
    ) {
      return <ReferralBanner goalInfo={goalInfo} />;
    }
  } else if (feed.type === 'gtp-educators') {
    const educators = [...(educatorPanelSection?.educators || [])];
    if (educatorPanelSection?.goalPageEducator) {
      educators.unshift(educatorPanelSection?.goalPageEducator);
    }
    return <GTPEducatorBlock educators={educators} />;
  } else if (isTestSeriesFeed(feedType)) {
    if (isGTP) {
      feedBlock.label = 'Tests';
    }
  } else if (isTopicFeed(feedType)) {
    minFeedCount = 6;
    feedCount = feed?.topologies?.length;
    seeAllLink = `/goal/${goalSlug}/${goalUID}/topics`;
    viewAllHref = '/goal/[goalSlug]/[goalUID]/topics';

    const renderCoursesInTopicsPage = (topic, topologyCoursesCount) => {
      const { upcomingCoursesCount, enrolledCoursesCount } = topic;
      let renderPrimaryCoursesCount = null;
      let renderSecondaryCoursesCount = '';
      const topologyCourses =
        topologyCoursesCount > 0
          ? textHelper.pluralize(topologyCoursesCount, 'course')
          : '';
      if (enrolledCoursesCount > 0) {
        renderPrimaryCoursesCount = (
          <EnrolledCourses>{`${enrolledCoursesCount} enrolled`}</EnrolledCourses>
        );
      } else if (topologyCoursesCount > 0) {
        renderPrimaryCoursesCount = topologyCourses;
      } else {
        renderPrimaryCoursesCount = 'Courses will be added soon';
      }
      if (upcomingCoursesCount > 0) {
        if (topologyCoursesCount === 0) {
          renderPrimaryCoursesCount = `${upcomingCoursesCount} upcoming`;
        } else
          renderSecondaryCoursesCount = `${Spacings.SPACING_1SPACE}• ${upcomingCoursesCount} upcoming`;
      }
      return (
        <CoursesInfo>
          {renderPrimaryCoursesCount}
          {renderSecondaryCoursesCount}
        </CoursesInfo>
      );
    };
    if (isGTP) {
      feedBlock.label = 'Subjects';
      minFeedCount = 0;
    }
    if (goalInfo.isRecordedGoal) {
      feedBlock.label = 'Video lessons';
    }
    feedProps.topic = {
      renderCourses: renderCoursesInTopicsPage
    };
  } else if (isLessonFeed(feedType) && feed.type === 'start_watching') {
    feedCount = feed.data.length;
    minFeedCount = 0;
    seeAllLink = `/goal/${goalSlug}/${goalUID}/topics`;
    viewAllHref = '/goal/[goalSlug]/[goalUID]/topics';
  }

  const viewAllLink = feedCount > minFeedCount ? seeAllLink : null;

  return (
    <FeedTypeBlock
      lastPrimarySource={lastPrimarySource}
      goalUID={goalInfo.uid}
      goalSlug={goalInfo.goalSlug}
      feedBlock={feedBlock}
      key={feed.type}
      goal={goalInfo}
      type={type}
      feedProps={feedProps}
      dataAnalytics={dataAnalytics}
      index={index}
      viewAllLink={viewAllLink}
      viewAllHref={viewAllHref}
      enroll={enroll}
      openExternalAppActivation={openExternalAppActivation}
    />
  );
};

export default RenderBrowseFeedBlock;
